/*
 * PolarisDX LANR field for the cart (drawer and cart page).
 * Documentation: docs/pdx-b2b-ordering.md
 *
 * BUSINESS CONTRACT (AP06.5, authoritative):
 *   LANR is OPTIONAL. An empty value or exactly nine digits is valid.
 *   Any other non-empty value blocks checkout and shows the error.
 *   Do NOT reintroduce a mandatory requirement (required, aria-required,
 *   asterisk) unless the business rule explicitly changes.
 *
 * Runtime model:
 *   - snippets/pdx-lanr-field.liquid renders a JSON config element and a
 *     <template> with the field markup once per page (layout/theme.liquid).
 *   - This script clones the template in front of every Horizon checkout
 *     control wrapper (.cart__ctas), so the drawer and the cart page share
 *     one implementation and one in-memory value.
 *   - Horizon re-renders the cart through the Section Rendering API and
 *     morphs the DOM, which drops injected nodes. A body-level
 *     MutationObserver re-injects; work is coalesced to one pass per
 *     animation frame. No polling.
 *   - Checkout interception happens at the document capture phase for both
 *     click and submit, ahead of Horizon's own handlers.
 *   - Persistence uses the Shopify cart attribute LANR via /cart/update.js.
 *     Only valid values are stored; invalid or cleared input writes an
 *     empty attribute, which Shopify drops.
 *
 * No external dependencies. Safe to include once; a second include is a no-op.
 */
(function () {
  'use strict';

  if (window.PdxLanr) return;

  var CONFIG_ID = 'pdx-lanr-config';
  var TEMPLATE_ID = 'pdx-lanr-template';
  var CTA_SELECTOR = '.cart__ctas';
  var FIELD_SELECTOR = '[data-pdx-lanr]';
  var INPUT_SELECTOR = '.pdx-lanr__input';
  var PATTERN = /^\d{9}$/;
  var DEBOUNCE_MS = 400;

  var configEl = document.getElementById(CONFIG_ID);
  var template = document.getElementById(TEMPLATE_ID);
  if (!configEl || !template || !('content' in template)) return;

  var config = {};
  try {
    config = JSON.parse(configEl.textContent) || {};
  } catch (error) {
    config = {};
  }

  var KEY = typeof config.key === 'string' && config.key ? config.key : 'LANR';
  var UPDATE_URL = typeof config.updateUrl === 'string' && config.updateUrl ? config.updateUrl : '/cart/update.js';
  var value = typeof config.value === 'string' ? config.value : '';
  var timer = null;
  var instance = 0;
  var scheduled = false;

  function isValid(candidate) {
    return candidate === '' || PATTERN.test(candidate);
  }

  function allFields() {
    return document.querySelectorAll(FIELD_SELECTOR);
  }

  function allInputs() {
    return document.querySelectorAll(INPUT_SELECTOR);
  }

  function persist(candidate) {
    clearTimeout(timer);
    timer = setTimeout(function () {
      var attributes = {};
      attributes[KEY] = isValid(candidate) ? candidate : '';
      fetch(UPDATE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attributes }),
      }).catch(function () {});
    }, DEBOUNCE_MS);
  }

  function setInvalid(invalid) {
    allFields().forEach(function (field) {
      field.classList.toggle('is-invalid', invalid);
      var input = field.querySelector(INPUT_SELECTOR);
      if (input) input.setAttribute('aria-invalid', invalid ? 'true' : 'false');
    });
  }

  function onInput(event) {
    var input = event.currentTarget;
    value = input.value.trim();
    setInvalid(false);
    allInputs().forEach(function (other) {
      if (other !== input) other.value = input.value;
    });
    persist(value);
  }

  function build() {
    instance += 1;
    var fragment = template.content.cloneNode(true);
    var field = fragment.querySelector(FIELD_SELECTOR);
    var input = fragment.querySelector(INPUT_SELECTOR);
    var label = fragment.querySelector('.pdx-lanr__label');
    var hint = fragment.querySelector('.pdx-lanr__hint');
    var errorEl = fragment.querySelector('.pdx-lanr__error');
    if (!field || !input || !label || !hint || !errorEl) return null;

    var suffix = '-' + instance;
    input.id = 'pdx-lanr-input' + suffix;
    hint.id = 'pdx-lanr-hint' + suffix;
    errorEl.id = 'pdx-lanr-error' + suffix;
    label.htmlFor = input.id;
    input.setAttribute('aria-describedby', hint.id + ' ' + errorEl.id);
    input.setAttribute('aria-invalid', 'false');
    input.name = 'attributes[' + KEY + ']';
    input.value = value;
    input.addEventListener('input', onInput);
    return field;
  }

  function inject() {
    var wrappers = document.querySelectorAll(CTA_SELECTOR);
    for (var i = 0; i < wrappers.length; i += 1) {
      var host = wrappers[i].parentElement;
      if (!host || host.querySelector(FIELD_SELECTOR)) continue;
      var field = build();
      if (field) host.insertBefore(field, wrappers[i]);
    }
  }

  function fieldFor(control) {
    var wrapper = control && control.closest ? control.closest(CTA_SELECTOR) : null;
    var host = wrapper ? wrapper.parentElement : null;
    return (host && host.querySelector(FIELD_SELECTOR)) || document.querySelector(FIELD_SELECTOR);
  }

  function validate(event, control) {
    var field = fieldFor(control);
    if (!field) return true;
    if (isValid(value)) return true;
    event.preventDefault();
    event.stopImmediatePropagation();
    setInvalid(true);
    var input = field.querySelector(INPUT_SELECTOR);
    if (input) {
      try {
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (error) {}
      input.focus();
    }
    return false;
  }

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      var control = target && target.closest ? target.closest('button[name="checkout"], a[href*="/checkout"]') : null;
      if (control) validate(event, control);
    },
    true
  );

  document.addEventListener(
    'submit',
    function (event) {
      var submitter = event.submitter;
      if (submitter && submitter.name === 'checkout') validate(event, submitter);
    },
    true
  );

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(function () {
      scheduled = false;
      inject();
    });
  }

  inject();
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('cart:update', schedule);

  window.PdxLanr = {
    version: '1.0.0',
    key: KEY,
    isValid: isValid,
    getValue: function () {
      return value;
    },
  };
})();
